(() => {
  if (customElements.get('hx-review-showcase')) return;
  class HxReviewShowcase extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-showcase-track]');
      this.rail = this.querySelector('[data-showcase-rail]');
      this.dialog = this.querySelector('dialog');
      this.all = this.querySelector('details');
      this.pauseButton = this.querySelector('[data-showcase-pause]');
      this.motion = matchMedia('(prefers-reduced-motion:reduce)');
      this.paused = this.motion.matches;
      this.visible = false;
      this.hovered = false;
      this.index = 0;
      this.items = this.readItems();
      this.renderItems();
      this.querySelectorAll('[data-showcase-step]').forEach(button => button.addEventListener('click', () => this.step(Number(button.dataset.showcaseStep)), {signal}));
      this.pauseButton.addEventListener('click', () => { this.paused = !this.paused; this.updatePause(); if (this.paused) this.finishScroll(); }, {signal});
      this.motion.addEventListener('change', () => { this.paused = this.motion.matches; this.updatePause(); }, {signal});
      this.rail.addEventListener('pointerenter', event => { if (event.pointerType !== 'touch') this.hovered = true; this.schedule(); }, {signal});
      this.rail.addEventListener('pointerleave', () => { this.hovered = false; this.schedule(); }, {signal});
      this.addEventListener('focusin', () => this.schedule(), {signal});
      this.addEventListener('focusout', () => queueMicrotask(() => this.schedule()), {signal});
      this.track.addEventListener('pointerdown', () => { this.interacting = true; this.paused = true; this.updatePause(); }, {signal});
      const release = () => {
        this.interacting = false;
        clearTimeout(this.snapTimer);
        this.snapTimer = setTimeout(() => this.finishScroll(), 180);
      };
      window.addEventListener('pointerup', release, {signal});
      window.addEventListener('pointercancel', release, {signal});
      this.track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        event.preventDefault();
        this.step(event.key === 'ArrowRight' ? 1 : -1);
      }, {signal});
      this.track.addEventListener('scroll', () => {
        if (this.loopWidth && !this.scrolling && this.track.scrollLeft >= this.loopWidth) this.track.scrollLeft %= this.loopWidth;
        this.updatePosition();
        if (this.paused && !this.interacting && !this.scrolling) {
          clearTimeout(this.snapTimer);
          this.snapTimer = setTimeout(() => this.finishScroll(), 180);
        }
      }, {passive:true, signal});
      this.track.addEventListener('scrollend', () => this.finishScroll(), {signal});
      this.track.addEventListener('click', event => {
        const button = event.target.closest('[data-showcase-read]');
        if (button) this.openReview(Number(button.dataset.showcaseRead), button);
      }, {signal});
      this.querySelector('[data-showcase-close]').addEventListener('click', () => this.dialog.close(), {signal});
      this.dialog.addEventListener('click', event => {
        const r = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) this.dialog.close();
      }, {signal});
      this.dialog.addEventListener('close', () => { this.returnFocus?.focus({preventScroll:true}); this.schedule(); }, {signal});
      this.querySelector('[data-showcase-all]').addEventListener('click', () => { this.all.open = true; }, {signal});
      document.addEventListener('visibilitychange', () => this.schedule(), {signal});
      this.visibility = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; this.schedule(); });
      this.visibility.observe(this.rail);
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.track);
      this.widget = this.querySelector('.hx-product-reviews-widget');
      this.observer = new MutationObserver(() => { clearTimeout(this.summaryTimer); this.summaryTimer = setTimeout(() => { this.readSummary(); this.syncPublishedReviews(); }, 120); });
      this.observer.observe(this.widget, {subtree:true, childList:true, attributes:true, attributeFilter:['data-average-rating', 'data-number-of-reviews']});
      this.readSummary();
      this.syncPublishedReviews();
      this.measure();
      this.updatePause();
    }
    readItems() {
      let source;
      try { source = JSON.parse(this.querySelector('[data-showcase-curation]').textContent); } catch { return []; }
      const seen = new Set();
      const decoder = document.createElement('textarea');
      return source.flatMap(item => {
        if (String(item.product_id) !== this.dataset.productId || !item.source || Number(item.source.star_rating) !== 5 || !item.text || item.text.startsWith('Translation missing') || seen.has(item.id)) return [];
        seen.add(item.id);
        decoder.innerHTML = item.text;
        return [{id:item.id, text:decoder.value, author:item.source.user_name, rating:Math.min(5, Math.max(0, Number(item.source.star_rating))), verified:item.source.verified_review === true}];
      });
    }
    renderItems() {
      const current = this.items[this.index]?.id;
      this.track.replaceChildren();
      this.items.forEach((item, index) => this.track.append(this.card(item, index)));
      this.cards = [...this.track.children];
      this.clones = this.cards.map(card => {
        const clone = card.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        clone.querySelectorAll('button').forEach(button => button.tabIndex = -1);
        this.track.append(clone);
        return clone;
      });
      this.index = Math.max(0, this.items.findIndex(item => item.id === current));
      this.rail.hidden = !this.items.length;
      if (!this.items.length) this.all.open = true;
    }
    syncPublishedReviews() {
      if (this.dialog.open || this.rail.contains(document.activeElement) || this.interacting) return;
      const source = [...this.widget.querySelectorAll('.jm-review-item, .jdgm-rev')];
      const clean = value => (value || '').replace(/\s+/g, ' ').trim();
      const fingerprint = item => clean(item.author).toLowerCase() + '|' + clean(item.text).toLowerCase();
      const merged = new Map(this.items.map(item => [item.id, item]));
      const fingerprints = new Set(this.items.map(fingerprint));
      let changed = false;
      source.forEach(review => {
        const rating = Number(review.querySelector('[data-score]')?.dataset.score || review.querySelector('.jm-star-rating')?.getAttribute('aria-label')?.match(/\d+(?:[.,]\d+)?/)?.[0]?.replace(',', '.'));
        if (rating !== 5) return;
        const body = review.querySelector('.jdgm-review-content__body-content, .jdgm-rev__body');
        const author = review.querySelector('.jm-reviewer-info__name, .jdgm-rev__author');
        const id = review.getAttribute('review-uuid') || review.dataset.id;
        const text = clean(body?.textContent);
        if (!id || !text || !author) return;
        const item = { id: 'published-' + id, author: clean(author.textContent), text, rating:5, verified: Boolean(review.querySelector('.jm-reviewer-info__verified-badge, .jdgm-rev__buyer-badge')) };
        const existing = merged.get(item.id);
        if (existing) {
          if (existing.text !== item.text || existing.author !== item.author) { merged.set(item.id, item); changed = true; }
        } else if (!fingerprints.has(fingerprint(item))) {
          merged.set(item.id, item); fingerprints.add(fingerprint(item)); changed = true;
        }
      });
      if (!changed) return;
      this.items = [...merged.values()];
      this.renderItems();
      this.measure();
    }
    stars(rating) {
      const stars = document.createElement('span');
      stars.className = 'hx-review-card-stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('[rating]', String(rating)));
      for (let n = 0; n < 5; n++) {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        if (n >= rating) svg.classList.add('is-empty');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'm12 2.8 2.85 5.77 6.37.93-4.61 4.49 1.09 6.35L12 17.34l-5.7 3 1.09-6.35L2.78 9.5l6.37-.93Z');
        svg.append(path); stars.append(svg);
      }
      return stars;
    }
    card(item, index) {
      const card = document.createElement('article');
      card.className = 'hx-review-showcase-card';
      card.dataset.reviewId = item.id;
      const top = document.createElement('div');
      top.className = 'hx-review-card-top';
      const quoteIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      quoteIcon.setAttribute('viewBox', '0 0 24 24');
      quoteIcon.setAttribute('aria-hidden', 'true');
      quoteIcon.classList.add('hx-review-card-quote');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'M4 13h5v7H2v-7c0-5 2-8 7-9v3c-3 1-5 3-5 6Zm13 0h5v7h-7v-7c0-5 2-8 7-9v3c-3 1-5 3-5 6Z');
      quoteIcon.append(path); top.append(this.stars(item.rating), quoteIcon);
      const quote = document.createElement('blockquote');
      quote.textContent = item.text;
      const author = document.createElement('div');
      author.className = 'hx-review-card-author';
      const avatar = document.createElement('span');
      avatar.className = 'hx-review-card-avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = Array.from(item.author.trim())[0]?.toLocaleUpperCase(document.documentElement.lang) || 'H';
      const identity = document.createElement('span');
      identity.className = 'hx-review-card-identity';
      identity.textContent = item.author;
      if (item.verified) {
        const verified = document.createElement('span');
        verified.className = 'hx-review-card-verified';
        verified.innerHTML = '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M10 2 12 3.2l2.4.3.9 2.2L17 7.2l-.3 2.4.3 2.5-1.7 1.6-.9 2.2L12 16l-2 1.2L8 16l-2.4-.1-.9-2.2L3 12.1l.3-2.5L3 7.2l1.7-1.5.9-2.2L8 3.2Z"/><path d="m6.4 9.6 2.4 2.5 4.8-5"/></svg>';
        verified.append(document.createTextNode(this.dataset.verifiedLabel)); identity.append(verified);
      }
      author.append(avatar, identity);
      card.append(top, quote);
      if (item.text.length > 100) {
        const more = document.createElement('button');
        more.type = 'button'; more.className = 'hx-review-card-more';
        more.dataset.showcaseRead = String(index); more.textContent = this.dataset.moreLabel;
        card.append(more);
      }
      card.append(author);
      return card;
    }
    readSummary() {
      const native = this.widget.querySelector('.jdgm-rev-widg[data-average-rating]');
      const summary = this.widget.querySelector('.jm-average-rating-display > .jm-cluster');
      const nativeRating = summary?.querySelector('.jm-star-rating')?.getAttribute('aria-label')?.match(/\d+(?:[.,]\d+)?/)?.[0];
      const nativeCount = summary?.querySelector(':scope > p:nth-of-type(2)')?.textContent.replace(/\D/g, '');
      const rating = Number(this.dataset.reviewRating || native?.dataset.averageRating || nativeRating?.replace(',', '.'));
      const count = Number(this.dataset.reviewCount || native?.dataset.numberOfReviews || nativeCount);
      if (!rating || !count) return;
      const locale = document.documentElement.lang;
      const formatted = new Intl.NumberFormat(locale, {minimumFractionDigits:1, maximumFractionDigits:1}).format(Math.ceil((rating - Number.EPSILON) * 10) / 10);
      this.querySelector('[data-showcase-rating]').textContent = this.dataset.ratingLabel.replace('[rating]', formatted);
      this.querySelector('[data-showcase-count]').textContent = this.dataset.countLabel.replace('[count]', new Intl.NumberFormat(locale).format(count));
      const stars = this.querySelector('[data-showcase-stars]');
      stars.style.setProperty('--review-star-empty', `${100 - rating * 20}%`);
      stars.setAttribute('aria-label', this.dataset.ratingLabel.replace('[rating]', formatted));
      this.querySelector('[data-showcase-score]').hidden = false;
      const average = native?.querySelector('.jdgm-rev-widg__summary-average') || summary?.querySelector(':scope > p:first-of-type');
      if (average) {
        const text = this.dataset.ratingLabel.replace('[rating]', formatted);
        if (average.textContent !== text) average.textContent = text;
      }
      const countText = summary?.querySelector(':scope > p:nth-of-type(2)');
      if (countText) {
        const text = this.dataset.countLabel.replace('[count]', new Intl.NumberFormat(locale).format(count));
        if (countText.textContent !== text) countText.textContent = text;
      }
    }
    measure() {
      if (!this.cards.length) return;
      this.clones.forEach(card => card.hidden = false);
      const width = this.clones[0].offsetLeft - this.cards[0].offsetLeft;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.loopWidth = width - gap > this.track.clientWidth - this.cards[0].offsetLeft * 2 ? width : 0;
      this.clones.forEach(card => card.hidden = !this.loopWidth);
      this.querySelector('.hx-review-showcase-controls').hidden = !this.loopWidth;
      if (!this.loopWidth) this.track.scrollLeft = 0;
      else this.track.scrollLeft = this.cards[this.index].offsetLeft - this.cards[0].offsetLeft;
      this.updatePosition(); this.schedule();
    }
    updatePosition() {
      if (!this.cards.length) return;
      const left = this.loopWidth ? this.track.scrollLeft % this.loopWidth : 0;
      const origin = this.cards[0].offsetLeft;
      this.index = this.cards.reduce((best, card, index) => Math.abs(card.offsetLeft - origin - left) < Math.abs(this.cards[best].offsetLeft - origin - left) ? index : best, 0);
      this.querySelector('[data-showcase-position]').textContent = this.dataset.positionLabel.replace('[current]', String(this.index + 1)).replace('[total]', String(this.cards.length));
    }
    step(direction) {
      if (!this.loopWidth || this.scrolling) return;
      this.paused = true; this.updatePause();
      const targetIndex = (this.index + direction + this.cards.length) % this.cards.length;
      let target = this.cards[targetIndex].offsetLeft - this.cards[0].offsetLeft;
      if (direction > 0 && targetIndex === 0) target = this.loopWidth;
      if (direction < 0 && this.index === 0) this.track.scrollLeft += this.loopWidth;
      this.scrolling = true;
      this.track.scrollTo({left:target, behavior:this.motion.matches ? 'instant' : 'smooth'});
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finishScroll(), 900);
    }
    finishScroll() {
      if (!this.scrolling) {
        if (this.paused && !this.interacting && this.loopWidth && !this.dialog.open) {
          const target = this.cards[this.index].offsetLeft - this.cards[0].offsetLeft;
          if (Math.abs(this.track.scrollLeft - target) > 1) this.track.scrollTo({left:target, behavior:this.motion.matches ? 'instant' : 'smooth'});
        }
        return;
      }
      this.scrolling = false;
      if (this.loopWidth) this.track.scrollLeft %= this.loopWidth;
      this.updatePosition(); this.schedule();
    }
    updatePause() {
      this.pauseButton.setAttribute('aria-pressed', String(this.paused));
      this.pauseButton.setAttribute('aria-label', this.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
      this.pauseButton.querySelector('path').setAttribute('d', this.paused ? 'm9 5 11 7-11 7Z' : 'M8 5v14M16 5v14');
      this.schedule();
    }
    schedule() {
      cancelAnimationFrame(this.frame); this.lastTime = null;
      if (!this.loopWidth || this.paused || this.motion.matches || !this.visible || this.hovered || document.hidden || this.dialog.open || this.rail.contains(document.activeElement)) return;
      this.autoPosition = this.track.scrollLeft;
      const tick = time => {
        if (this.lastTime !== null) {
          this.autoPosition = (this.autoPosition + Math.min(time - this.lastTime, 64) * .023) % this.loopWidth;
          this.track.scrollLeft = this.autoPosition;
        }
        this.lastTime = time;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    openReview(index, button) {
      const item = this.items[index];
      if (!item) return;
      this.returnFocus = button;
      this.querySelector('[data-showcase-dialog-stars]').replaceChildren(this.stars(item.rating));
      this.querySelector('[data-showcase-dialog-text]').textContent = item.text;
      this.querySelector('[data-showcase-dialog-author]').textContent = item.author;
      this.dialog.showModal(); this.schedule();
    }
    disconnectedCallback() {
      this.controller?.abort(); this.controller = null;
      this.visibility?.disconnect(); this.resize?.disconnect(); this.observer?.disconnect();
      cancelAnimationFrame(this.frame); clearTimeout(this.scrollTimer); clearTimeout(this.snapTimer); clearTimeout(this.summaryTimer);
    }
  }
  customElements.define('hx-review-showcase', HxReviewShowcase);
})();

